import { aggregate, stillFailing } from './aggregate';
import { ApiError, UNAUTHORIZED, type ApiClient } from './api';
import { PLATFORM, PROBE_CONCURRENCY, RUN_DEADLINE_MS, UNKNOWN } from './config';
import { deviceInfo } from './deviceInfo';
import { displayRegion, ipInfo as loadIpInfo } from './ipInfo';
import { awaitOnline, isOnline } from './network';
import { pool } from './pool';
import { probeReachable } from './probe';
import { sleep } from './timeout';
import type { CheckResult, Link, MeasurementContext, ResultRow, RunConfig, SubmitResponse } from './types';

/**
 * One whole check cycle, in the one order that works.
 *
 * This file is to this app what `PrepareRun` + `CheckRunner` are to the Android
 * one, and the ordering constraints are the same ones, for the same reasons:
 *
 * ```
 * wait for a network        ← before config, not after (see below)
 *   → GET device-api/config ← how many rounds, how long between them
 *   → snapshot the network  ← labels the measurement, not the submission
 *   → GET device-api/links
 *   → round 1 probes everything; each later round only what is still failing
 *   → aggregate: healthy once = healthy; dead only if every round failed
 *   → POST device-api/results   ← exactly once per cycle
 * ```
 *
 * **Waiting for the network comes first.** Ask for config before the gate and a
 * device that is offline when its cycle comes due always errors, always falls
 * back to defaults, and therefore *never* receives the configuration the server
 * set for it. The irony is exact: the devices on the weakest signal — the ones
 * most likely to need their own settings — would be the only ones that never
 * get them.
 *
 * **The snapshot comes after the config call**, because it must describe the
 * network the cycle is about to measure on, and the config call is the last
 * thing that could have taken time.
 *
 * What is **not** here, and cannot be: Android's browser verification round
 * (SPEC §14 #8). A WebView the app controls has no equivalent inside Safari, so
 * a link behind a JavaScript bot-wall is reported by whatever the probe saw and
 * nothing rescues it.
 */

export type Phase =
  | { kind: 'idle' }
  | { kind: 'waiting-for-network' }
  | { kind: 'fetching' }
  | { kind: 'checking'; checked: number; total: number; alive: number; dead: number; round: number; totalRounds: number }
  | { kind: 'between-rounds'; nextRound: number; totalRounds: number; secondsLeft: number }
  | { kind: 'submitting' }
  | { kind: 'done'; saved: number; skipped: number }
  | { kind: 'failed'; reason: string };

export interface CycleOutcome {
  config: RunConfig;
  context: MeasurementContext;
  results: CheckResult[];
  /** Measured but not reported — see `aggregate`'s `dropped`. Counted, never sent. */
  droppedCount: number;
  response: SubmitResponse;
  finishedAt: number;
}

/** Thrown when the cycle refuses to submit rather than submit something untrue. */
export class CycleAborted extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'CycleAborted';
  }
}

/** Thrown on `401`. The key is dead; the caller must stop, not retry. */
export class KeyRevoked extends Error {
  constructor() {
    super('device key rejected (401)');
    this.name = 'KeyRevoked';
  }
}

export interface RunOptions {
  api: ApiClient;
  onPhase?: (phase: Phase) => void;
  /** Surfaces each link's latest verdict to the UI while the round is still running. */
  onResult?: (result: CheckResult, round: number) => void;
  signal?: AbortSignal;
  deadlineMs?: number;
  concurrency?: number;
  probe?: typeof probeReachable;
}

export async function runCycle(options: RunOptions): Promise<CycleOutcome> {
  const {
    api,
    onPhase = () => {},
    onResult = () => {},
    signal,
    deadlineMs = RUN_DEADLINE_MS,
    concurrency = PROBE_CONCURRENCY,
    probe = probeReachable,
  } = options;

  const abortIfCancelled = () => {
    if (signal?.aborted) throw new CycleAborted('cancelled');
  };

  onPhase({ kind: 'waiting-for-network' });
  if (!(await awaitOnline(undefined, signal))) {
    // Nothing measured, nothing sent. A batch produced with no network is not a
    // measurement of the links; submitting one is worse than submitting nothing.
    throw new CycleAborted('offline');
  }
  abortIfCancelled();

  onPhase({ kind: 'fetching' });
  const config = await wrap401(() => api.config());
  const context = await snapshot();
  const links = await wrap401(() => api.links());
  abortIfCancelled();

  if (links.length === 0) {
    throw new CycleAborted('no links to check');
  }

  const totalRounds = Math.max(1, config.checkRounds);
  const gapMs = config.roundIntervalSeconds * 1000;
  const rounds: CheckResult[][] = [];
  const deadline = Date.now() + deadlineMs;

  // Links with no verdict yet. Round 1 gets all of them; each later round gets
  // only what is still failing, because a link that answered once is healthy by
  // the aggregation rule and asking again could only cost traffic — and on a
  // metered field SIM that traffic is real money.
  let pending: Link[] = links;

  for (let round = 1; round <= totalRounds; round++) {
    if (pending.length === 0) break;
    if (Date.now() >= deadline) break;
    abortIfCancelled();

    const resolvedBefore = links.length - pending.length;
    const results: CheckResult[] = [];
    rounds.push(results);

    const emit = () => {
      const alive = resolvedBefore + results.filter((r) => r.healthStatus === 'healthy').length;
      onPhase({
        kind: 'checking',
        checked: results.length,
        total: pending.length,
        alive,
        // Counted over links that have actually been measured at least once.
        // Defining `dead` as `total − alive` instead would count every link
        // nobody has reached yet as dead, so a fresh run opens on "0 alive /
        // 200 dead" and crawls back — reading as if the whole list were down.
        dead: (round === 1 ? resolvedBefore + results.length : links.length) - alive,
        round,
        totalRounds,
      });
    };
    emit();

    await pool(
      pending,
      concurrency,
      async (link) => {
        // The deadline is checked per link rather than per round: a round over a
        // long list can overrun it on its own, and a link probed after the
        // deadline still costs the submit its time.
        if (Date.now() >= deadline || signal?.aborted) {
          return null;
        }
        const outcome = await probe(link.url);
        return { link, ...outcome } satisfies CheckResult;
      },
      (result) => {
        if (!result) return;
        results.push(result);
        onResult(result, round);
        emit();
      },
    );

    // Links the deadline skipped are simply absent from `results`. They stay in
    // `pending` for a later round if there is one, and if there is not they fall
    // to `aggregate`'s `dropped` — measured too few times to condemn, so not
    // reported at all. Never marked unhealthy to fill the gap: that would put a
    // measurement nobody took into the batch.
    const measured = new Set(results.map((r) => r.link.id));
    pending = [...stillFailing(results), ...pending.filter((l) => !measured.has(l.id))];

    if (pending.length === 0 || round === totalRounds) break;
    if (Date.now() >= deadline) break;

    // The gap between rounds is the point of the whole design — it is what lets
    // a two-second signal drop pass before the link is asked again.
    await countdown(gapMs, round + 1, totalRounds, onPhase, signal);
  }

  abortIfCancelled();
  const { results, dropped } = aggregate(rounds, totalRounds);

  if (results.length === 0) {
    throw new CycleAborted('nothing measured');
  }

  // Last gate before the wire. If the device has gone offline while the rounds
  // were running, the failures collected are ours, not the links'. Waiting
  // briefly and then refusing is the honest end to the cycle; the next tick
  // retries the whole thing from scratch (the design is idempotent — SPEC §9).
  if (!isOnline() && !(await awaitOnline(undefined, signal))) {
    throw new CycleAborted('offline before submit');
  }

  onPhase({ kind: 'submitting' });
  const info = deviceInfo();
  const response = await wrap401(() =>
    api.submit({
      // From the snapshot taken at the start of the cycle, never re-read here.
      network: context.network,
      region: context.region,
      carrier: context.carrier,
      model: info.model,
      platform: PLATFORM,
      appVersion: info.appVersion,
      meta: info.meta,
      ipInfo: context.ipInfo,
      results: results.map(toRow),
    }),
  );

  onPhase({ kind: 'done', saved: response.saved, skipped: response.skipped });
  return {
    config,
    context,
    results,
    droppedCount: dropped.length,
    response,
    finishedAt: Date.now(),
  };
}

/**
 * `statusCode` is hard-coded `null`, and that is the point (SPEC §6).
 *
 * There is no number to send. Deriving a plausible one from `responseMs`, or
 * writing `200` because the probe resolved, would put a fabricated fact into a
 * row the backend stores as measured. Empty and honest beats populated and wrong.
 */
function toRow(result: CheckResult): ResultRow {
  const row: ResultRow = {
    brandLinkId: Number(result.link.id),
    healthStatus: result.healthStatus,
    statusCode: null,
    responseMs: result.responseMs,
  };
  if (result.error) row.error = result.error;
  return row;
}

/** Which network this cycle is about to measure on. Never a guess — see `UNKNOWN`. */
async function snapshot(): Promise<MeasurementContext> {
  const ip = await loadIpInfo();
  return {
    // Always unknown on iOS: Safari has no Network Information API, so wifi and
    // 5G are indistinguishable from in here (SPEC §14 #5).
    network: UNKNOWN,
    region: displayRegion(ip) ?? UNKNOWN,
    carrier: ip?.isp ?? UNKNOWN,
    ipInfo: ip?.raw ?? null,
  };
}

/** Re-throws a `401` as `KeyRevoked` so callers branch on the type, not the number. */
async function wrap401<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (error instanceof ApiError && error.status === UNAUTHORIZED) throw new KeyRevoked();
    throw error;
  }
}

/** The between-rounds wait, ticked once a second so the screen can show it. */
async function countdown(
  totalMs: number,
  nextRound: number,
  totalRounds: number,
  onPhase: (phase: Phase) => void,
  signal?: AbortSignal,
): Promise<void> {
  const until = Date.now() + totalMs;
  for (;;) {
    const left = until - Date.now();
    if (left <= 0 || signal?.aborted) return;
    onPhase({
      kind: 'between-rounds',
      nextRound,
      totalRounds,
      secondsLeft: Math.ceil(left / 1000),
    });
    await sleep(Math.min(1000, left), signal);
  }
}
